import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { forexApi, type OnboardingSummary } from '@/services/forexApi';

const ownerLabel = { account: '账号资料', platform: '平台', node: '终端节点', operator: '管理员', none: '' };
const actionLabel = { wait: '系统正在继续处理，请等待状态更新', resubmit_credentials: '按提示补充账号资料',
  retry: '可重试当前开通任务', contact_operator: '请联系管理员核对原因与未决义务', none: '' };

export default function OnboardingProgress({ accountId, submissionRevision, followLabel, followError = false, onProgress, onResubmit }: { accountId: string; submissionRevision?: number; followLabel?: string | null; followError?: boolean; onProgress?: (id: string, value: OnboardingSummary) => void; onResubmit?: () => void }) {
  const { accessToken } = useAuth();
  const [progress, setProgress] = useState<OnboardingSummary | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (action: 'retry' | 'cancel') => {
    if (!progress?.revision) return;
    setBusy(true);
    try { await forexApi.onboardingAction(accountId, action, progress.revision, accessToken); setProgress(await forexApi.onboardingProgress(accountId, accessToken)); }
    catch { setError('操作未完成，请刷新状态后重试'); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const controller = new AbortController();
    const load = async () => {
      try {
        const value = await forexApi.onboardingProgress(accountId, accessToken, controller.signal);
        if (!stopped && (value.submission_revision ?? 0) >= (submissionRevision ?? 0)) { setProgress(value); onProgress?.(accountId, value); setError(''); }
      } catch {
        if (!stopped) setError('开通进度暂不可用，请检查登录状态或稍后重试');
      } finally {
        if (!stopped) timer = setTimeout(() => void load(), 5000);
      }
    };
    void load();
    return () => { stopped = true; controller.abort(); clearTimeout(timer); };
  }, [accountId, accessToken, submissionRevision, onProgress]);
  if (error) return <p role="alert" className="text-red-500 text-xs py-3">{error}</p>;
  if (!progress) return null;
  return <div className="rounded-xl bg-indigo-50 p-3 my-3 text-sm" aria-live="polite">
    <p>{progress.stage === 'connected' ? '接入已完成' : progress.stage_label}</p>
    {progress.execution_status_label && <p className="text-xs mt-1">{progress.execution_status_label}</p>}
    {!!progress.reason_messages?.length && <p className="text-xs mt-1">{Array.from(new Set(progress.reason_messages)).join("；")}</p>}
    {progress.diagnostics && progress.diagnostics.owner !== 'none' && <p className="text-xs mt-1">
      处理方：{ownerLabel[progress.diagnostics.owner]}。{actionLabel[progress.diagnostics.next_action]}
    </p>}
    {progress.stage === 'connected' && <>
      <p className="text-xs mt-1">当前连接：{progress.connection_status === 'online' ? '在线' : progress.connection_status === 'offline' ? '离线' : '待核对'}</p>
      <p className="text-xs mt-1">当前跟随：{followError ? '待核对' : followLabel || '尚未设置'}</p>
    </>}
    {progress.stage === 'failed' && onResubmit && <div className="mt-2"><button disabled={busy || progress.can_resubmit !== true} onClick={onResubmit} className="text-indigo-600 disabled:text-gray-400">重新提交凭据</button>{progress.can_resubmit !== true && <p className="text-xs text-gray-500 mt-1">当前尚不满足重新提交条件，请联系管理员核验实例清理和执行状态。{progress.resubmit_block_reason && `（${progress.resubmit_block_reason}）`}</p>}</div>}
    {progress.revision && progress.can_retry && (!progress.diagnostics || progress.diagnostics.retryable) && <button disabled={busy} onClick={() => void act('retry')} className="mr-3 text-indigo-600">重试开通</button>}
    {progress.revision && progress.can_cancel && <button disabled={busy} onClick={() => void act('cancel')} className="text-gray-600">取消开通</button>}
    {progress.error_code && !progress.diagnostics && <p className="text-xs mt-1">开通未完成：{progress.error_code}。请联系管理员核对。</p>}
  </div>;
}
